/* eslint-disable no-undef */
import { BDGoogleMap } from './bdGoogleMap'
import { Equipment } from './equipment'
import { timestamp, isGoogleEventsReady, getClientID } from '@/utilities'
import config from '@/config'
const { cdn } = config

export class BDMap {
  constructor (dom, config, callback) {
    this.uiDom = dom
    this.mapDom = null
    this.googleMap = null
    this.equipment = null
    this.savedJson = {}
    this.config = config
    this.callback = callback
  }

  initialize () {
    this.prepareMap()

    return new Promise((resolve, reject) => {
      this.googleMap = new BDGoogleMap(
        this.mapDom,
        this.config.googleApiKey,
        this.config.geocodeApiKey,
        this.config
      )

      this.googleMap.initialize().then(() => {
        this.drawUI()
        resolve()
      })
    })
  }

  saveonstart () {
    this.savedJson = {
      center: {
        lat: this.googleMap.map.getCenter().lat(),
        lng: this.googleMap.map.getCenter().lng()
      },
      equipmentSize: {
        width: this.equipment.width,
        height: this.equipment.height
      },
      equipmentPoints: this.equipment.points().map((pt) => {
        return {
          lat: pt.lat(),
          lng: pt.lng()
        }
      }),
      mapType: this.googleMap.map.getMapTypeId(),
      mapTilt: this.googleMap.map.getTilt(),
      mapHeading: this.googleMap.map.getHeading() | 0,
      zoom: this.googleMap.map.getZoom()
    }
    return this.savedJson
  }

  createStaticMap () {
    let mapUrl = 'https://maps.googleapis.com/maps/api/staticmap'
    mapUrl += `?center=${this.googleMap.map
      .getCenter()
      .lat()},${this.googleMap.map.getCenter().lng()}`
    mapUrl += '&size=600x400'
    mapUrl += '&zoom=20'
    mapUrl += `&maptype=${this.googleMap.map.getMapTypeId()}`
    mapUrl += `&fov=${this.googleMap.map.getTilt()}`
    mapUrl += `&heading=${this.googleMap.map.getHeading()}`
    let equipmentPath = 'path=color:0x0000FFFF|weight:5|fillcolor:0x0000FFB0'
    this.equipment.points().forEach((pt) => {
      equipmentPath += `|${pt.lat()},${pt.lng()}`
    })
    mapUrl += `&${equipmentPath}`

    // create canvas to draw the map on
    const canvas = document.createElement('canvas')
    canvas.width = 600
    canvas.height = 400
    const ctx = canvas.getContext('2d')
    const img = new Image()
    // to bypass crossOrigin issue
    img.crossOrigin = 'anonymous'
    const make = () => {
      img.src = mapUrl += `&key=${this.config.googleApiKey}`
      img.onload = function () {
        ctx.drawImage(img, 0, 0, 600, 400)
      }
    }
    make()

    return mapUrl
  }

  // post data to server
  postToServer = async () => {
    const data = {
      imageurl: this.createStaticMap(),
      jsonScript: this.savedJson
    }
    // this contains the static map iamge URI and runs the saveDumpsterPlacementStaticMapUri method on the service page to save the data to vuex
    this.callback(data)
  }

  load (savedJson) {
    this.googleMap.center(savedJson.center)
    this.addEquipment(
      savedJson.equipmentSize.width,
      savedJson.equipmentSize.height
    )

    const equipmentPoints = savedJson.equipmentPoints.map((pt) => {
      return new google.maps.LatLng(pt.lat, pt.lng)
    })

    this.equipment.setPoints(equipmentPoints)
    this.drawEquipment()
    this.googleMap.map.setZoom(savedJson.zoom)
    this.googleMap.map.setMapTypeId(savedJson.mapType)
    this.googleMap.map.setTilt(savedJson.mapTilt)
    this.googleMap.map.setHeading(savedJson.mapHeading | 0)
  }

  reset () {
    this.googleMap.resetCenter()
  }

  loadAddress (address) {
    return this.googleMap.centerOnAddress(address)
  }

  addEquipment (width, height, dumpsterName) {
    this.equipment = new Equipment(
      width,
      height,
      this.googleMap.map.getCenter(),
      this.googleMap.map.getProjection()

    )
    this.drawEquipment()
    this.equipment.dumpsterName = dumpsterName

    google.maps.event.addListener(this.googleMap.polygon, 'dragstart', (e) => {
      this.googleMap.drawDragMarker()
    })

    google.maps.event.addListener(this.googleMap.dragMarker, 'click', (e) => {
      // this.googleMap.drawDragMarker(this.rotateEquipment(5))
      this.googleMap.drawDragMarker(this.equipment.calculateCenterPoint())
    })

    google.maps.event.addListener(this.googleMap.polygon, 'dragend', (e) => {
      this.equipment.setPoints(this.googleMap.polygon.getPath().getArray())
      this.googleMap.drawDragMarker(this.equipment.calculateCenterPoint())
    })
  }

  getDumpsterDescription (size) {
    const dumpsterSizeMap = {
      11: { length: 14, height: 3.5 },
      14: { length: 14, height: 4 },
      18: { length: 16, height: 4.5 },
      28: { length: 22, height: 4.5 },
      35: { length: 22, height: 6 },
      50: { length: 22, height: 8 }
    }
    return dumpsterSizeMap[size]
  }

  dumpsterDescripton (dumpsterName) {
    const size = parseInt(dumpsterName)
    const width = 7.5
    let dimensions
    if (size <= 11) {
      dimensions = this.getDumpsterDescription(11)
    } else if (size <= 14) {
      dimensions = this.getDumpsterDescription(14)
    } else if (size <= 18) {
      dimensions = this.getDumpsterDescription(18)
    } else if (size <= 28) {
      dimensions = this.getDumpsterDescription(28)
    } else if (size <= 35) {
      dimensions = this.getDumpsterDescription(35)
    } else if (size <= 50) {
      dimensions = this.getDumpsterDescription(50)
    }

    const dumpsterinfo = document.createElement('div')
    dumpsterinfo.className = 'dumpster-info'
    dumpsterinfo.innerHTML = `${dumpsterName} Yard Dumpster<br>Avg. Dimensions <br>${dimensions.length}'L x ${width}'W x ${dimensions.height}'H`
    this.googleMap.map.controls[google.maps.ControlPosition.TOP_LEFT].push(dumpsterinfo)
  }

  rotateEquipment (deg) {
    this.equipment.rotate(deg)
    this.drawEquipment()
  }

  prepareMap () {
    // Add the DOM for the map
    this.mapDom = document.createElement('div')
    this.mapDom.className = 'bdmap'
    this.uiDom.appendChild(this.mapDom)
  }

  // show overlay
  showOverlay = () => {
    document.getElementsByClassName('bd-overlay')[0].style.display = 'block'
  }

  hideOverlay = () => {
    document.getElementsByClassName('bd-overlay')[0].style.display = 'none'
  }

  // these are the buttons and other elements on the map
  drawUI () {
    // header instructions
    const headingInstructions = document.createElement('div')
    headingInstructions.className = 'bd-heading'
    headingInstructions.innerHTML = this.config.headingInstructions
    this.uiDom.insertBefore(headingInstructions, this.mapDom)

    // reset button
    const resetLocationButton = document.createElement('button')
    resetLocationButton.className = 'bd-reset-button bd-onpoint-buttons'
    resetLocationButton.innerHTML = `Reset <img src="${cdn}Reset.svg">`
    resetLocationButton.setAttribute('type', 'button')
    this.googleMap.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(
      resetLocationButton
    )
    resetLocationButton.addEventListener('click', () => this.load(this.savedJson))

    // rotate button
    const rotateDumpsterButton = document.createElement('button')
    rotateDumpsterButton.className = 'bd-rotate-button bd-onpoint-buttons'
    rotateDumpsterButton.innerHTML = `Rotate <img src="${cdn}Rotate.svg">`
    rotateDumpsterButton.setAttribute('type', 'button')
    rotateDumpsterButton.addEventListener('click', () =>
      this.rotateEquipment(this.config.rotationAmount)
    )
    this.googleMap.map.controls[google.maps.ControlPosition.RIGHT_CENTER].push(
      rotateDumpsterButton
    )

    // overlay
    const overlay = document.createElement('div')
    overlay.className = 'bd-overlay'
    overlay.style.display = 'none'
    this.uiDom.appendChild(overlay)
    overlay.addEventListener('click', (e) => { if (e.target.className === 'bd-overlay') { this.hideOverlay() } })

    // overlay text
    const overlayText = document.createElement('div')
    overlayText.className = 'bd-overlay-text'
    overlayText.innerHTML = '<div><strong> Thank You</strong> <p>Your dumpster placement has been confirmed.</p></div>'
    overlay.appendChild(overlayText)
    // overlay image
    const overlayImage = document.createElement('img')
    overlayImage.className = 'bd-overlay-image'
    overlayImage.src = `${cdn}confirm-icon.svg`
    overlayText.appendChild(overlayImage)
    // overlay close button
    const overlayClose = document.createElement('button')
    overlayClose.className = 'bd-overlay-close'
    overlayClose.innerHTML = 'x'
    overlayClose.setAttribute('type', 'button')
    overlayText.appendChild(overlayClose)
    overlayClose.addEventListener('click', () => this.hideOverlay())
    // overlay continue button
    const overlayContinue = document.createElement('button')
    overlayContinue.className = 'bd-overlay-continue'
    overlayContinue.innerHTML = 'Continue'
    overlayContinue.setAttribute('type', 'button')
    overlayText.appendChild(overlayContinue)
    overlayContinue.addEventListener('click', () => this.hideOverlay())

    // confirm button
    const submitButton = document.createElement('button')
    submitButton.className = 'bd-submit-button bd-onpoint-buttons'
    submitButton.innerHTML = `Confirm <img src="${cdn}icons-checkmark-confirm.svg">`
    submitButton.setAttribute('type', 'button')
    this.googleMap.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(
      submitButton)

    submitButton.addEventListener('click', (e) => {
      if (isGoogleEventsReady()) {
        const time = timestamp()

        window.gtag('event', 'Ecommerce - OnPointConfirmation', {
          event_category: 'Ecommerce',
          event_label: `${time}~${window.location.href}`,
          send_to: process.env.VUE_APP_UA_ID
        })

        window.gtag('event', 'Ecommerce - OnPointConfirmation', {
          event_category: 'Ecommerce',
          timestamp: time,
          bd_client_id: getClientID(),
          send_to: process.env.VUE_APP_GA4_ID
        })
      }

      this.postToServer()
    })
  }

  drawEquipment () {
    this.googleMap.drawPolygon(this.equipment.points())
    this.googleMap.drawDragMarker(this.equipment.calculateCenterPoint())
  }
}
